import { useState, useCallback, useEffect } from "react";
import { __ } from "@/i18n";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardAction,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useWidgetStore } from "@/stores/widget-store";
import { saveWidgetConfig } from "@/services/widget";
import { toast } from "sonner";

import { StatementStep1 } from "@/components/statement/statement-step1";
import { StatementStep2 } from "@/components/statement/statement-step2";
import { StatementGenerated } from "@/components/statement/statement-generated";
import { StatementLink } from "@/components/statement/statement-link";
import { SectionCard } from "@/components/ui/section-card";
import type { Step1Form, Step2Form } from "@/constants/statement-constants";
import {
  DEFAULT_STEP1,
  DEFAULT_STEP2,
} from "@/constants/statement-constants";

type TabState = "idle" | "step1" | "step2" | "generating" | "generated";

export function StatementGeneratorTab() {
  const {
    config,
    setStatementEnabled,
    setStatementUrl,
    setStatementDisplayInWidget,
    setStatementGeneratedDate,
    setStatementFormData,
  } = useWidgetStore();

  const statement = config.modules.statement;
  const hasStatement = !!(statement.formData?.companyName || statement.generatedDate);

  const [tabState, setTabState] = useState<TabState>(hasStatement ? "generated" : "idle");
  const [isSaving, setIsSaving] = useState(false);

  // ── Initialise form from DB (persisted formData) or defaults ────────────────
  const savedFormData = statement.formData;

  const [step1, setStep1] = useState<Step1Form>(() => ({
    companyName: savedFormData?.companyName || DEFAULT_STEP1.companyName,
    businessEmail: savedFormData?.businessEmail || DEFAULT_STEP1.businessEmail,
    website:
      savedFormData?.website ||
      (typeof window !== "undefined" ? window.location.origin : ""),
  }));

  const [step2, setStep2] = useState<Step2Form>(() => ({
    wcagStandard: savedFormData?.wcagStandard || DEFAULT_STEP2.wcagStandard,
    conformanceStatus: savedFormData?.conformanceStatus || DEFAULT_STEP2.conformanceStatus,
  }));

  // Re-sync if the config loads asynchronously after mount
  useEffect(() => {
    if (savedFormData?.companyName || savedFormData?.businessEmail) {
      setStep1({
        companyName: savedFormData.companyName,
        businessEmail: savedFormData.businessEmail,
        website: savedFormData.website,
      });
      setStep2({
        wcagStandard: savedFormData.wcagStandard,
        conformanceStatus: savedFormData.conformanceStatus,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.modules.statement.formData]);

  // ── Save header ─────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveWidgetConfig(config);
      toast.success( __( 'Changes saved successfully.', 'accessibility-widget' ) );
    } catch {
      toast.error( __( 'Failed to save changes.', 'accessibility-widget' ) );
    } finally {
      setIsSaving(false);
    }
  }, [config]);

  // ── Generate ─────────────────────────────────────────────────────────────────
  const handleGenerate = useCallback(async () => {
    setTabState("generating");

    const today = new Date().toISOString().slice(0, 10);
    const formData = {
      companyName: step1.companyName,
      businessEmail: step1.businessEmail,
      website: step1.website,
      wcagStandard: step2.wcagStandard,
      conformanceStatus: step2.conformanceStatus,
    };

    setStatementGeneratedDate(today);
    setStatementFormData(formData);

    try {
      await saveWidgetConfig({
        ...config,
        modules: {
          ...config.modules,
          statement: {
            ...statement,
            generatedDate: today,
            formData,
          },
        },
      });
      toast.success( __( 'Statement generated and saved.', 'accessibility-widget' ) );
    } catch {
      toast.error( __( 'Statement generated but could not be saved. Use Save changes.', 'accessibility-widget' ) );
    }

    setTabState("generated");
  }, [step1, step2, config, statement, setStatementGeneratedDate, setStatementFormData]);

  return (
    <div className="cy:max-w-7xl cy:mx-auto cy:px-4 cy:sm:px-6 cy:lg:px-8 cy:pt-8">
      <Card className="cy:rounded-lg cy:gap-0 cy:shadow-none!">
        <CardHeader className="cy:border-b cy:border-gray-200 cy:pb-6">
          <CardTitle className="cy:text-xl">{__( 'Accessibility statement', 'accessibility-widget' )}</CardTitle>
          <CardDescription>
            {__( "Generate and manage your website's accessibility statement.", 'accessibility-widget' )}
          </CardDescription>
          <CardAction>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="cy:rounded cy:text-base!"
            >
              {isSaving ? __( 'Saving...', 'accessibility-widget' ) : __( 'Save changes', 'accessibility-widget' )}
            </Button>
          </CardAction>
        </CardHeader>

        <CardContent className="cy:p-0">
          <div className="cy:max-w-7xl cy:mx-auto cy:px-4 cy:sm:px-6 cy:lg:px-8 cy:py-6 cy:space-y-4">

            {tabState === "idle" && (
              <SectionCard className="cy:border cy:border-gray-200">
                <div className="cy:flex cy:items-start cy:justify-between cy:gap-6">
                  <div className="cy:space-y-1">
                    <h3 className="cy:text-base cy:font-semibold cy:text-foreground">
                      {__( 'Generate statement', 'accessibility-widget' )}
                    </h3>
                    <p className="cy:text-sm cy:text-muted-foreground">
                      {__( "Don't have an accessibility statement? Use our generator to create a professional statement.", 'accessibility-widget' )}
                    </p>
                  </div>
                  <Button
                    onClick={() => setTabState("step1")}
                    className="cy:rounded cy:flex-shrink-0"
                  >
                    {__( 'Get started', 'accessibility-widget' )}
                  </Button>
                </div>
              </SectionCard>
            )}

            {tabState === "step1" && (
              <StatementStep1
                step1={step1}
                onChange={setStep1}
                onNext={() => setTabState("step2")}
              />
            )}

            {tabState === "step2" && (
              <StatementStep2
                step2={step2}
                onChange={setStep2}
                onBack={() => setTabState("step1")}
                onGenerate={handleGenerate}
              />
            )}

            {tabState === "generating" && (
              <SectionCard className="cy:border cy:border-gray-200">
                <div className="cy:flex cy:items-center cy:gap-3 cy:py-4">
                  <svg
                    className="cy:w-5 cy:h-5 cy:animate-spin cy:text-primary"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="cy:opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="cy:opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  <span className="cy:text-sm cy:text-muted-foreground">
                    {__( 'Generating statement...', 'accessibility-widget' )}
                  </span>
                </div>
              </SectionCard>
            )}

            {tabState === "generated" && (
              <StatementGenerated
                generatedDate={statement.generatedDate}
                formData={statement.formData}
                displayInWidget={statement.displayInWidget}
                onEdit={() => setTabState("step1")}
                onToggleDisplay={(checked) => {
                  setStatementDisplayInWidget(checked);
                  // Mutually exclusive: turning this on turns off the link toggle
                  if (checked) setStatementEnabled(false);
                }}
              />
            )}

            <StatementLink
              enabled={statement.enabled}
              url={statement.url}
              onToggle={(checked) => {
                setStatementEnabled(checked);
                // Mutually exclusive: turning this on turns off display-in-widget
                if (checked) setStatementDisplayInWidget(false);
              }}
              onUrlChange={setStatementUrl}
            />

          </div>
        </CardContent>
      </Card>
    </div>
  );
}
